import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  MapPin, 
  Users, 
  AlertTriangle,
  Filter,
  X,
  CheckCircle,
  Clock,
  Euro,
  Truck,
  Eye,
  FileText,
  Loader2,
  Printer
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

export default function PlanningGlobalPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFoodTruck, setSelectedFoodTruck] = useState("");
  const [showConflicts, setShowConflicts] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear
      });
      if (selectedFoodTruck) {
        params.append("food_truck_id", selectedFoodTruck);
      }
      
      const response = await fetch(`${API_URL}/planning-global?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        toast.error("Erreur lors du chargement du planning");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear, selectedFoodTruck]);

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const clearFilters = () => {
    setSelectedFoodTruck("");
  };

  // Stats
  const stats = useMemo(() => {
    if (!data) return { totalEvents: 0, totalParticipants: 0, conflicts: 0 };
    
    let totalEvents = 0;
    let totalParticipants = 0;
    
    data.planning.forEach(day => {
      totalEvents += day.events.length;
      day.events.forEach(event => {
        totalParticipants += event.participants.length;
      });
    });
    
    return {
      totalEvents,
      totalParticipants,
      conflicts: data.conflicts?.length || 0
    };
  }, [data]);

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case "paye":
        return <Badge className="bg-green-100 text-green-700 text-xs">Payé</Badge>;
      case "exempte":
        return <Badge className="bg-blue-100 text-blue-700 text-xs">Exempté</Badge>;
      case "en_attente":
        return <Badge className="bg-amber-100 text-amber-700 text-xs">En attente</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700 text-xs">N/A</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "EEEE d MMMM", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  // Export PDF for a specific day
  const exportDayToPDF = async (dayData) => {
    setExportingPDF(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Header
      doc.setFillColor(122, 45, 45); // Primary color
      doc.rect(0, 0, pageWidth, 25, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Planning Global - Food Truck United", margin, 16);
      
      // Date
      const dateFormatted = format(parseISO(dayData.date), "EEEE d MMMM yyyy", { locale: fr });
      doc.setFontSize(12);
      doc.text(dateFormatted.charAt(0).toUpperCase() + dateFormatted.slice(1), pageWidth - margin, 16, { align: "right" });
      
      yPos = 35;

      // Events table header
      doc.setTextColor(0, 0, 0);
      doc.setFillColor(249, 247, 245);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 10, "F");
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      
      const colWidths = [70, 50, 90, 30, 27];
      const headers = ["Événement", "Lieu", "Food Trucks", "Places", "Type"];
      let xPos = margin + 2;
      
      headers.forEach((header, i) => {
        doc.text(header, xPos, yPos + 7);
        xPos += colWidths[i];
      });
      
      yPos += 12;

      // Events rows
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);

      dayData.events.forEach((event, idx) => {
        // Check if we need a new page
        const rowHeight = Math.max(10, event.participants.length * 5 + 5);
        if (yPos + rowHeight > pageHeight - 20) {
          doc.addPage();
          yPos = margin;
        }

        // Alternate row background
        if (idx % 2 === 0) {
          doc.setFillColor(255, 255, 255);
        } else {
          doc.setFillColor(252, 250, 248);
        }
        doc.rect(margin, yPos - 2, pageWidth - 2 * margin, rowHeight, "F");

        xPos = margin + 2;

        // Event title
        doc.setFont("helvetica", "bold");
        doc.text(event.titre.substring(0, 35), xPos, yPos + 4);
        xPos += colWidths[0];

        // Location
        doc.setFont("helvetica", "normal");
        doc.text((event.lieu || "").substring(0, 25), xPos, yPos + 4);
        xPos += colWidths[1];

        // Participants
        if (event.participants.length === 0) {
          doc.setTextColor(150, 150, 150);
          doc.text("Aucun participant", xPos, yPos + 4);
          doc.setTextColor(0, 0, 0);
        } else {
          event.participants.slice(0, 4).forEach((p, pIdx) => {
            doc.text(`• ${p.nom_food_truck.substring(0, 20)}`, xPos, yPos + 4 + pIdx * 4);
          });
          if (event.participants.length > 4) {
            doc.setTextColor(100, 100, 100);
            doc.text(`+${event.participants.length - 4} autres`, xPos, yPos + 4 + 4 * 4);
            doc.setTextColor(0, 0, 0);
          }
        }
        xPos += colWidths[2];

        // Places
        if (event.nombre_food_trucks_requis) {
          const places = `${event.participants.length}/${event.nombre_food_trucks_requis}`;
          doc.text(places, xPos + 5, yPos + 4);
        } else {
          doc.text("-", xPos + 10, yPos + 4);
        }
        xPos += colWidths[3];

        // Type
        const typeLabel = event.type_evenement === "collectif" ? "Collectif" : "Individuel";
        doc.text(typeLabel, xPos, yPos + 4);

        yPos += rowHeight + 2;
      });

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Généré le ${format(new Date(), "dd/MM/yyyy à HH:mm", { locale: fr })}`, margin, pageHeight - 10);
      doc.text(`${dayData.events.length} événement(s)`, pageWidth - margin, pageHeight - 10, { align: "right" });

      // Save
      const filename = `Planning_${format(parseISO(dayData.date), "yyyy-MM-dd")}.pdf`;
      doc.save(filename);
      toast.success(`PDF "${filename}" téléchargé !`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  // Export all days of the month
  const exportMonthToPDF = async () => {
    if (!data?.planning?.length) {
      toast.error("Aucun événement à exporter");
      return;
    }
    
    setExportingPDF(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;

      data.planning.forEach((dayData, dayIdx) => {
        if (dayIdx > 0) doc.addPage();
        
        let yPos = margin;

        // Header
        doc.setFillColor(122, 45, 45);
        doc.rect(0, 0, pageWidth, 25, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("Planning Global - Food Truck United", margin, 16);
        
        const dateFormatted = format(parseISO(dayData.date), "EEEE d MMMM yyyy", { locale: fr });
        doc.setFontSize(12);
        doc.text(dateFormatted.charAt(0).toUpperCase() + dateFormatted.slice(1), pageWidth - margin, 16, { align: "right" });
        
        yPos = 35;

        // Table header
        doc.setTextColor(0, 0, 0);
        doc.setFillColor(249, 247, 245);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 10, "F");
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        
        const colWidths = [70, 50, 90, 30, 27];
        const headers = ["Événement", "Lieu", "Food Trucks", "Places", "Type"];
        let xPos = margin + 2;
        
        headers.forEach((header, i) => {
          doc.text(header, xPos, yPos + 7);
          xPos += colWidths[i];
        });
        
        yPos += 12;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        dayData.events.forEach((event, idx) => {
          const rowHeight = Math.max(10, event.participants.length * 5 + 5);
          if (yPos + rowHeight > pageHeight - 20) {
            doc.addPage();
            yPos = margin;
          }

          if (idx % 2 === 0) {
            doc.setFillColor(255, 255, 255);
          } else {
            doc.setFillColor(252, 250, 248);
          }
          doc.rect(margin, yPos - 2, pageWidth - 2 * margin, rowHeight, "F");

          xPos = margin + 2;
          doc.setFont("helvetica", "bold");
          doc.text(event.titre.substring(0, 35), xPos, yPos + 4);
          xPos += colWidths[0];

          doc.setFont("helvetica", "normal");
          doc.text((event.lieu || "").substring(0, 25), xPos, yPos + 4);
          xPos += colWidths[1];

          if (event.participants.length === 0) {
            doc.setTextColor(150, 150, 150);
            doc.text("Aucun participant", xPos, yPos + 4);
            doc.setTextColor(0, 0, 0);
          } else {
            event.participants.slice(0, 4).forEach((p, pIdx) => {
              doc.text(`• ${p.nom_food_truck.substring(0, 20)}`, xPos, yPos + 4 + pIdx * 4);
            });
            if (event.participants.length > 4) {
              doc.setTextColor(100, 100, 100);
              doc.text(`+${event.participants.length - 4} autres`, xPos, yPos + 4 + 4 * 4);
              doc.setTextColor(0, 0, 0);
            }
          }
          xPos += colWidths[2];

          if (event.nombre_food_trucks_requis) {
            doc.text(`${event.participants.length}/${event.nombre_food_trucks_requis}`, xPos + 5, yPos + 4);
          } else {
            doc.text("-", xPos + 10, yPos + 4);
          }
          xPos += colWidths[3];

          doc.text(event.type_evenement === "collectif" ? "Collectif" : "Individuel", xPos, yPos + 4);
          yPos += rowHeight + 2;
        });

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${dayIdx + 1}/${data.planning.length}`, margin, pageHeight - 10);
        doc.text(`${dayData.events.length} événement(s)`, pageWidth - margin, pageHeight - 10, { align: "right" });
      });

      const filename = `Planning_${MONTHS[selectedMonth - 1]}_${selectedYear}.pdf`;
      doc.save(filename);
      toast.success(`PDF "${filename}" téléchargé !`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  // Export month to Excel
  const exportMonthToExcel = () => {
    if (!data?.planning?.length) {
      toast.error("Aucun événement à exporter");
      return;
    }
    
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Create data rows
      const rows = [];
      
      // Header
      rows.push(["Date", "Événement", "Lieu", "Food Trucks", "Places", "Type"]);
      
      // Data
      data.planning.forEach((day) => {
        const dateFormatted = format(parseISO(day.date), "EEEE d MMMM yyyy", { locale: fr });
        
        day.events.forEach((event) => {
          const foodTrucks = event.participants.map(p => p.nom_food_truck).join(", ") || "Aucun";
          const places = event.nombre_food_trucks_requis 
            ? `${event.participants.length}/${event.nombre_food_trucks_requis}`
            : "-";
          
          rows.push([
            dateFormatted,
            event.titre,
            event.lieu || "-",
            foodTrucks,
            places,
            event.type_evenement === "collectif" ? "Collectif" : "Individuel"
          ]);
        });
      });
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(rows);
      
      // Set column widths
      ws["!cols"] = [
        { wch: 25 }, // Date
        { wch: 35 }, // Événement
        { wch: 25 }, // Lieu
        { wch: 40 }, // Food Trucks
        { wch: 10 }, // Places
        { wch: 12 }, // Type
      ];
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Planning");
      
      // Save
      const filename = `Planning_${MONTHS[selectedMonth - 1]}_${selectedYear}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(`Excel "${filename}" téléchargé !`);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Erreur lors de l'export Excel");
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-global-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-secondary">Planning Global</h1>
          <p className="text-muted-foreground">Vue d'ensemble des événements et food trucks</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={exportMonthToPDF}
            disabled={exportingPDF || !data?.planning?.length}
            className="border-primary text-primary hover:bg-primary hover:text-white"
          >
            {exportingPDF ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
          <Button
            variant="outline"
            onClick={exportMonthToExcel}
            disabled={!data?.planning?.length}
            className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
          >
            <FileText className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-full">
              <Calendar className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Événements ce mois</p>
              <p className="text-2xl font-bold text-secondary">{stats.totalEvents}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <Truck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Participations totales</p>
              <p className="text-2xl font-bold text-secondary">{stats.totalParticipants}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-white border-[#E5E0DC] cursor-pointer transition-all ${
            stats.conflicts > 0 ? "hover:border-red-300" : ""
          }`}
          onClick={() => stats.conflicts > 0 && setShowConflicts(!showConflicts)}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`p-3 rounded-full ${stats.conflicts > 0 ? "bg-red-100" : "bg-gray-100"}`}>
              <AlertTriangle className={`w-6 h-6 ${stats.conflicts > 0 ? "text-red-600" : "text-gray-400"}`} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Conflits détectés</p>
              <p className={`text-2xl font-bold ${stats.conflicts > 0 ? "text-red-600" : "text-secondary"}`}>
                {stats.conflicts}
              </p>
            </div>
            {stats.conflicts > 0 && (
              <Button variant="ghost" size="sm" className="ml-auto text-red-600">
                <Eye className="w-4 h-4" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Conflicts Alert */}
      {showConflicts && data?.conflicts?.length > 0 && (
        <Card className="bg-red-50 border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Conflits de planning ({data.conflicts.length})
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto text-red-600"
                onClick={() => setShowConflicts(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.conflicts.map((conflict, idx) => (
                <div key={idx} className="flex items-center gap-4 p-3 bg-white rounded-lg border border-red-200">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">{conflict.nom_food_truck}</p>
                    <p className="text-sm text-red-600">
                      {formatDate(conflict.date)} - Assigné à {conflict.events.length} événements :
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {conflict.events.map((e, i) => (
                        <Badge 
                          key={i} 
                          variant="outline" 
                          className="border-red-300 text-red-700 cursor-pointer hover:bg-red-100"
                          onClick={() => navigate(`/events/${e.id}`)}
                        >
                          {e.titre}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            {/* Month/Year Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 min-w-[200px] justify-center">
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, idx) => (
                      <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((year) => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="h-8 w-px bg-[#E5E0DC] hidden md:block" />
            
            {/* Food Truck Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedFoodTruck || "all"} onValueChange={(v) => setSelectedFoodTruck(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrer par Food Truck" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les Food Trucks</SelectItem>
                  {data?.food_trucks?.map((ft) => (
                    <SelectItem key={ft.id} value={ft.id}>{ft.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedFoodTruck && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Planning Table */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Planning {MONTHS[selectedMonth - 1]} {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.planning?.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun événement validé pour cette période</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data?.planning?.map((day) => (
                <div key={day.date} className="border border-[#E5E0DC] rounded-lg overflow-hidden">
                  {/* Date Header */}
                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-3 border-b border-[#E5E0DC] flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-secondary capitalize">
                        {formatDate(day.date)}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {day.events.length} événement{day.events.length > 1 ? "s" : ""}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => exportDayToPDF(day)}
                      disabled={exportingPDF}
                      className="text-primary hover:bg-primary/10"
                      title="Exporter ce jour en PDF"
                    >
                      {exportingPDF ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Printer className="w-4 h-4" />
                      )}
                      <span className="ml-2 hidden sm:inline">PDF</span>
                    </Button>
                  </div>
                  
                  {/* Events Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F9F7F5]">
                        <tr>
                          <th className="text-left px-4 py-2 text-sm font-medium text-secondary">Événement</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-secondary">Lieu</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-secondary">Food Trucks</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-secondary">Places</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-secondary">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {day.events.map((event) => (
                          <tr key={event.id} className="border-t border-[#E5E0DC] hover:bg-[#F9F7F5]">
                            {/* Event Title */}
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-secondary">{event.titre}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  <Badge 
                                    variant={event.type_evenement === "collectif" ? "default" : "secondary"}
                                    className="text-xs"
                                  >
                                    {event.type_evenement}
                                  </Badge>
                                  {event.statut && event.statut !== "valide" && (
                                    <Badge 
                                      variant="outline"
                                      className="text-xs bg-amber-50 text-amber-700 border-amber-300"
                                    >
                                      {event.statut === "en_attente_participation" ? "Sondage en cours" : 
                                       event.statut === "en_attente_selection" ? "Sélection en cours" :
                                       event.statut === "brouillon" ? "Brouillon" :
                                       event.statut === "en_cours" ? "En cours" :
                                       event.statut === "sondage_envoye" ? "Sondage envoyé" :
                                       event.statut}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </td>
                            
                            {/* Location */}
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-1 text-sm text-muted-foreground">
                                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p>{event.lieu}</p>
                                  {event.adresse && <p className="text-xs">{event.adresse}</p>}
                                </div>
                              </div>
                            </td>
                            
                            {/* Participants */}
                            <td className="px-4 py-3">
                              {event.participants.length === 0 ? (
                                <span className="text-sm text-muted-foreground italic">Aucun participant</span>
                              ) : (
                                <div className="space-y-1">
                                  {event.participants.map((p) => (
                                    <div key={p.food_truck_id} className="flex items-center gap-2">
                                      <Truck className="w-4 h-4 text-primary flex-shrink-0" />
                                      <span className="text-sm font-medium">{p.nom_food_truck}</span>
                                      {getPaymentStatusBadge(p.statut_paiement)}
                                      {p.montant_du > 0 && p.statut_paiement !== "paye" && (
                                        <span className="text-xs text-amber-600">{p.montant_du}€</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            
                            {/* Places */}
                            <td className="px-4 py-3 text-center">
                              {event.places_restantes !== null ? (
                                <div className="flex flex-col items-center">
                                  <span className={`text-lg font-bold ${
                                    event.places_restantes === 0 ? "text-green-600" : 
                                    event.places_restantes <= 2 ? "text-amber-600" : "text-secondary"
                                  }`}>
                                    {event.participants.length}/{event.nombre_food_trucks_requis}
                                  </span>
                                  {event.places_restantes > 0 ? (
                                    <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                                      {event.places_restantes} place{event.places_restantes > 1 ? "s" : ""} restante{event.places_restantes > 1 ? "s" : ""}
                                    </Badge>
                                  ) : (
                                    <Badge className="text-xs bg-green-100 text-green-700">
                                      Complet
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            
                            {/* Actions */}
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/events/${event.id}`)}
                                className="text-primary hover:text-primary-hover"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Détails
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
